import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useRef, useState } from 'react';

import { Backdrop, CircularProgress, SxProps } from '@mui/material';

const DEFAULT_SX: SxProps = {
    backgroundColor: 'common.white',
    color: 'info.main',
};

type LoaderContext = {
    loader: boolean | SxProps;
    setLoader: Dispatch<SetStateAction<boolean | SxProps>>;
};
const LoaderContext = createContext<LoaderContext>({} as LoaderContext);

export function LoaderProvider({ children }: { children: ReactNode }) {
    const [loader, setLoader] = useState<boolean | SxProps>(DEFAULT_SX);

    // mantém o último sx aplicado para que a estilização persista
    // durante a animação de saída do Backdrop (evita piscar o layout)
    const lastSxRef = useRef<SxProps>(DEFAULT_SX);

    if (typeof loader === 'object') {
        lastSxRef.current = loader;
    } else if (loader === true) {
        lastSxRef.current = {};
    }

    return (
        <LoaderContext.Provider value={{ loader, setLoader }}>
            <Backdrop
                open={Boolean(loader)}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    color: 'common.white',
                    ...lastSxRef.current,
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {children}
        </LoaderContext.Provider>
    );
}

/**
 * Atualiza o estado do loader global da aplicação.
 * Pode definir seu valor via parâmetro ou via função setLoader retornada.
 */
export const useLoaderContext = (loader?: boolean | SxProps) => {
    const { setLoader } = useContext(LoaderContext);

    useEffect(() => {
        if (loader !== undefined) {
            setLoader(loader);

            return () => setLoader(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loader]);

    return { setLoader };
};
